import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  AvailableSchedulePeriod,
  formatMinutes,
  ITimeSlotTimestamp,
  LocalAvailableTime,
  splitByDay,
  TIME_INPUT_FORMAT,
} from '@techspert-io/schedule-calendar';
import moment from 'moment-timezone';
import { SkeletonBlockComponent } from '../../../../../../../shared/components/skeleton-block/skeleton-block.component';
import { TimezonePreferenceService } from '../../../../../../../shared/services/timezone-preference.service';
import { calculateOffsetLabel } from '../utils/calculate-offset';
import { filterBookablePeriods } from '../utils/filter-bookable-periods';

type AllowDelete = {
  allowDelete: boolean;
};

type ExpertPerspective = {
  start: string;
  end: string;
  tooltip: string;
};

type DisplayInterviewPeriod = LocalAvailableTime &
  AllowDelete & {
    /** YYYY-MM-DD */
    date: string;
    /** The row as UTC timestamps, as its times repeat when the clocks go back */
    range: ITimeSlotTimestamp;
    expertPerspective?: ExpertPerspective;
  };

type RequestAdditionalAvailabilityPeriod = AvailableSchedulePeriod &
  Partial<AllowDelete>;

@Component({
  selector: 'app-request-additional-availability-interview-period-table',
  templateUrl:
    './request-additional-availability-interview-period-table.component.html',
  styleUrls: [
    './request-additional-availability-interview-period-table.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatTooltipModule,
    MatIconModule,
    MatButtonModule,
    DatePipe,
    SkeletonBlockComponent,
  ],
})
export class RequestAdditionalAvailabilityInterviewPeriodTableComponent {
  private sort: MatSort;
  @ViewChild(MatSort) set matSort(ms: MatSort) {
    this.sort = ms;
    this.setupTableSortingAndPagination(this.dataSource());
  }
  private paginator: MatPaginator;
  @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
    this.paginator = mp;
    this.setupTableSortingAndPagination(this.dataSource());
  }

  readonly isLoading = input(true);
  readonly availableSchedulePeriods = input<AvailableSchedulePeriod[]>([]);
  readonly additionalAvailableSchedulePeriods = input<
    AvailableSchedulePeriod[]
  >([]);
  readonly callDuration = input(60);
  readonly expertTimezone = input<string>();

  readonly removeTime = output<AvailableSchedulePeriod>();

  readonly displayedColumns = ['date', 'start', 'end', 'actions'];
  readonly dataSource = computed<MatTableDataSource<DisplayInterviewPeriod>>(
    () =>
      this.setupTableData([
        ...this.availableSchedulePeriods(),
        ...this.additionalAvailableSchedulePeriods().map((period) => ({
          ...period,
          allowDelete: true,
        })),
      ])
  );

  private readonly timezoneName = inject(TimezonePreferenceService)
    .selectedTimezone;

  remove({ range, isPreferred, allowDelete }: DisplayInterviewPeriod) {
    if (!allowDelete) {
      return;
    }

    this.removeTime.emit({ ...range, isPreferred });
  }

  private setupTableData(periods: RequestAdditionalAvailabilityPeriod[]) {
    const filteredPeriods = filterBookablePeriods(periods, this.callDuration());

    const dataSource = new MatTableDataSource(
      filteredPeriods
        .sort(
          (a, b) => a.start.localeCompare(b.start) || a.end.localeCompare(b.end)
        )
        .flatMap((period) =>
          splitByDay(period, this.timezoneName()).map(
            ({ date, start, end, range }) => ({
              date,
              start: formatMinutes(start),
              end: formatMinutes(end),
              isPreferred: period.isPreferred,
              allowDelete: !!period.allowDelete,
              range,
              expertPerspective: this.constructExpertPerspective(range),
            })
          )
        )
    );

    return this.setupTableSortingAndPagination(dataSource);
  }

  private constructExpertPerspective(
    clientPerspective: ITimeSlotTimestamp
  ): ExpertPerspective | undefined {
    const expertTimezone = this.expertTimezone();
    const clientTimezone = this.timezoneName();

    if (!expertTimezone || !clientTimezone) {
      return;
    }
    const expertStart = moment(clientPerspective.start).tz(expertTimezone);

    const offset = calculateOffsetLabel(
      moment(clientPerspective.start).tz(clientTimezone),
      expertStart
    );

    if (!offset) {
      return;
    }

    return {
      start: this.formatExpertTime(expertStart, offset),
      end: this.formatExpertTime(
        moment(clientPerspective.end).tz(expertTimezone),
        offset
      ),
      tooltip: [offset, `(${expertTimezone})`].join(' '),
    };
  }

  private formatExpertTime(time: moment.Moment, offset: string) {
    return [offset, time.format(TIME_INPUT_FORMAT)].join(' ');
  }

  private setupTableSortingAndPagination(
    dataSource: MatTableDataSource<DisplayInterviewPeriod>
  ) {
    if (!dataSource) {
      return;
    }

    dataSource.paginator = this.paginator;
    dataSource.sort = this.sort;

    return dataSource;
  }
}
